import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import {
  Activity, Brain, Music, Flower2, Waves, Palette, Utensils, Users, Compass,
  Info, Dumbbell, HeartPulse, Sparkles, Accessibility, HandHeart, Landmark, Building2, HeartHandshake,
} from "lucide-react";
import { useLang } from "@/i18n";
import { Reveal } from "@/lib/motion";

const Overline = ({ children }) => (
  <span className="inline-block text-xs uppercase tracking-[0.22em] font-bold text-terracotta-600 mb-4">
    {children}
  </span>
);

const HISTORY_IMG = "https://images.pexels.com/photos/20860592/pexels-photo-20860592.jpeg";

/* ---------------- HISTORY ---------------- */
export function History() {
  const { t } = useLang();
  const h = t.history;
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", reduce ? "-8%" : "12%"]);

  return (
    <section id="history" ref={ref} className="py-24 md:py-32 bg-bone" data-testid="history-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 overflow-hidden rounded-[2rem] aspect-[4/5]">
              <motion.img style={{ y }} src={HISTORY_IMG} alt="AEMBAIX" className="w-full h-[115%] object-cover" loading="lazy" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <Reveal><Overline>{h.overline}</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif font-light text-pine-900 text-4xl md:text-5xl leading-tight tracking-tight mb-8">
                {h.title}
              </h2>
            </Reveal>
            <Reveal delay={0.1}><p className="text-lg leading-relaxed text-pine-700 mb-5">{h.body}</p></Reveal>
            <Reveal delay={0.15}><p className="text-lg leading-relaxed text-pine-700">{h.body2}</p></Reveal>

            <div className="mt-14 relative pl-8 border-l-2 border-bone-200">
              {h.milestones.map((m, i) => (
                <Reveal key={i} delay={0.05 * i} className="relative pb-12 last:pb-0">
                  <span className="absolute -left-[41px] top-1.5 w-4 h-4 rounded-full bg-terracotta ring-4 ring-bone" />
                  <div className="flex items-baseline gap-4 mb-2">
                    <span className="font-serif text-4xl md:text-5xl text-pine-900 leading-none">{m.year}</span>
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-sage">{m.label}</span>
                  </div>
                  <p className="text-pine-700 leading-relaxed max-w-lg">{m.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- MISSION (numbered manifesto) ---------------- */
const MISSION_ICONS = [Info, Dumbbell, HeartPulse, Sparkles, Accessibility];
export function Mission() {
  const { t } = useLang();
  const m = t.mission;
  return (
    <section id="mission" className="py-24 md:py-32 bg-pine-900 text-bone relative overflow-hidden" data-testid="mission-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
        <div className="max-w-3xl mb-16">
          <Reveal><span className="inline-block text-xs uppercase tracking-[0.22em] font-bold text-terracotta mb-4">{m.overline}</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight tracking-tight text-bone mb-6">{m.title}</h2>
          </Reveal>
          <Reveal delay={0.1}><p className="text-lg leading-relaxed text-sage">{m.intro}</p></Reveal>
        </div>

        <div className="divide-y divide-pine-800 border-t border-pine-800">
          {m.items.map((it, i) => {
            const Icon = MISSION_ICONS[i] || Sparkles;
            return (
              <Reveal key={i} delay={0.04 * i}>
                <div className="group grid md:grid-cols-12 gap-4 md:gap-8 items-center py-8 transition-colors duration-300 hover:bg-pine-800/40 -mx-5 px-5 rounded-xl">
                  <span className="md:col-span-2 font-serif text-5xl md:text-6xl text-sage/40 group-hover:text-terracotta transition-colors duration-300 leading-none">{it.n}</span>
                  <div className="md:col-span-1 hidden md:flex"><Icon className="w-7 h-7 text-sage" /></div>
                  <h3 className="md:col-span-4 font-sans text-xl md:text-2xl font-semibold text-bone">{it.title}</h3>
                  <p className="md:col-span-5 text-sage leading-relaxed">{it.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- SERVICES (bento) ---------------- */
const SERVICE_ICONS = [Activity, Brain, Music, Flower2, Waves, Palette, Utensils, Users, Compass];
const SPANS = [
  "md:col-span-2 md:row-span-2",
  "md:col-span-2",
  "md:col-span-1",
  "md:col-span-1",
  "md:col-span-2",
  "md:col-span-1",
  "md:col-span-1",
  "md:col-span-2",
  "md:col-span-1",
];
export function Services() {
  const { t } = useLang();
  const s = t.services;
  return (
    <section id="services" className="py-24 md:py-32 bg-bone-100" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <Reveal><Overline>{s.overline}</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif font-light text-pine-900 text-4xl md:text-5xl leading-tight tracking-tight mb-5">{s.title}</h2>
          </Reveal>
          <Reveal delay={0.1}><p className="text-lg leading-relaxed text-pine-700">{s.intro}</p></Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[minmax(150px,auto)] gap-5">
          {s.list.map((svc, i) => {
            const Icon = SERVICE_ICONS[i] || Sparkles;
            const featured = i === 0;
            return (
              <Reveal key={i} delay={0.03 * i} className={SPANS[i]}>
                <div
                  className={`group h-full flex flex-col justify-between rounded-3xl p-7 border-2 transition-colors duration-300 ${
                    featured
                      ? "bg-pine-900 border-pine-900 text-bone"
                      : "bg-bone border-bone-200 hover:border-pine-900"
                  }`}
                  data-testid={`service-card-${i}`}
                >
                  <div className={`grid place-items-center w-12 h-12 rounded-2xl mb-6 transition-colors duration-300 ${featured ? "bg-terracotta text-white" : "bg-bone-100 text-pine-900 group-hover:bg-terracotta group-hover:text-white"}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className={`font-sans font-semibold tracking-tight mb-2 ${featured ? "text-2xl text-bone" : "text-xl text-pine-900"}`}>{svc.name}</h3>
                    <p className={`${featured ? "text-sage text-base" : "text-pine-700 text-sm"} leading-relaxed`}>{svc.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 flex items-start gap-4 rounded-2xl bg-bone border border-bone-200 p-6 max-w-4xl">
            <Landmark className="w-6 h-6 text-terracotta shrink-0 mt-0.5" />
            <p className="text-pine-700 leading-relaxed">{s.note}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- COMMUNITY ---------------- */
const COMMUNITY_IMG = "https://images.pexels.com/photos/6973123/pexels-photo-6973123.jpeg";
export function Community() {
  const { t } = useLang();
  const c = t.community;
  return (
    <section id="community" className="py-24 md:py-32 bg-bone" data-testid="community-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <Reveal><Overline>{c.overline}</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif font-light text-pine-900 text-4xl md:text-5xl leading-tight tracking-tight mb-6">{c.title}</h2>
            </Reveal>
            <Reveal delay={0.1}><p className="text-lg leading-relaxed text-pine-700 mb-8">{c.body}</p></Reveal>
            <div className="flex flex-wrap gap-3 mb-8">
              {c.events.map((e, i) => (
                <Reveal key={i} delay={0.03 * i}>
                  <span className="inline-flex items-center rounded-full border-2 border-pine-900 px-4 py-2 text-sm font-semibold text-pine-900">{e}</span>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.1}>
              <div className="rounded-2xl bg-bone-100 border border-bone-200 p-6">
                <p className="font-serif italic text-xl text-pine-900 leading-snug">{c.highlight}</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="rounded-[2rem] overflow-hidden aspect-[4/3] shadow-[0_20px_60px_rgba(26,46,37,0.12)]">
              <img src={COMMUNITY_IMG} alt={c.title} className="w-full h-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- OPEN SPACE ---------------- */
export function OpenSpace() {
  const { t } = useLang();
  const o = t.openSpace;
  return (
    <section className="py-24 md:py-28 bg-terracotta text-white relative overflow-hidden" data-testid="openspace-section">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
        <Reveal><HandHeart className="w-12 h-12 mx-auto mb-6 text-white/90" /></Reveal>
        <Reveal delay={0.05}><span className="inline-block text-xs uppercase tracking-[0.22em] font-bold text-white/80 mb-5">{o.overline}</span></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight mb-8">{o.title}</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="text-xl leading-relaxed text-white/95 max-w-3xl mx-auto">{o.body}</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- FUNDING ---------------- */
const FUND_ICONS = [Users, Landmark, HeartHandshake];
const PARTNER_ICONS = [Building2, Landmark, Users];
export function Funding() {
  const { t } = useLang();
  const f = t.funding;
  return (
    <section className="py-24 md:py-32 bg-bone-100" data-testid="funding-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-14">
          <Reveal><Overline>{f.overline}</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif font-light text-pine-900 text-4xl md:text-5xl leading-tight tracking-tight mb-5">{f.title}</h2>
          </Reveal>
          <Reveal delay={0.1}><p className="text-lg text-pine-700">{f.body}</p></Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-16">
          {f.sources.map((src, i) => {
            const Icon = FUND_ICONS[i] || HeartHandshake;
            return (
              <Reveal key={i} delay={0.05 * i}>
                <div className="h-full rounded-3xl bg-bone border border-bone-200 p-8 hover:border-pine-900 transition-colors duration-300">
                  <div className="grid place-items-center w-12 h-12 rounded-2xl bg-pine-900 text-bone mb-6"><Icon className="w-6 h-6" /></div>
                  <h3 className="font-sans text-xl font-semibold text-pine-900 mb-2">{src.title}</h3>
                  <p className="text-pine-700 leading-relaxed">{src.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <p className="text-xs uppercase tracking-[0.22em] font-bold text-sage mb-6">{f.partnersLabel}</p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {f.partners.map((p, i) => {
            const Icon = PARTNER_ICONS[i] || Building2;
            return (
              <Reveal key={i} delay={0.05 * i}>
                <div className="flex items-center gap-4 rounded-2xl bg-bone border-2 border-bone-200 px-6 py-5">
                  <Icon className="w-7 h-7 text-terracotta shrink-0" />
                  <span className="font-serif text-xl text-pine-900">{p}</span>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
